/**
 * DI (Directional Influence) particle effects — entry burst, ambient idle,
 * input-active streaks, and resolve launch choreography.
 */

import { configParticle, GEM_PALETTES, DEFAULT_GEM_PALETTE, type ParticleCore } from './types';

/**
 * DI Entry — time-freeze moment when DI window opens.
 * 6-layer Sakurai choreography: converging ring motes -> radial speed lines ->
 * dark contrast cavity -> soft core flash -> delayed clockwise orbit sparks -> shockwave.
 *
 * Sakurai audit:
 * - Flash communicates impact instantly (layers 3+4)
 * - Dark + bright contrast (layer 3 near-black vs layer 4 bright)
 * - Crisp straight lines vs soft curves (layer 2 speed lines vs layer 1 motes)
 * - Stagger timing (layer 5 delayed orbit)
 * - Offset spawns — no center overlap waste
 * - Freeze-immune — animates through hitstop
 * - Scale: ring spans 80px (3x tank radius) — no candle flames
 */
export function spawnDIEntry(core: ParticleCore, cx: number, cy: number, playerColor: number): void {
  // === Layer 1: Converging ring motes — bright particles pull INWARD toward tank ===
  // Communicates "time freezing" — energy gathering, not dispersing
  const moteCount = 16;
  for (let i = 0; i < moteCount; i++) {
    const angle = (i / moteCount) * Math.PI * 2;
    const dist = 65 + Math.random() * 25; // spawn at ring distance
    const spawnX = cx + Math.cos(angle) * dist;
    const spawnY = cy + Math.sin(angle) * dist;
    // Velocity points INWARD (toward center)
    const speed = 3 + Math.random() * 2;
    const moteColor = Math.random() < 0.3 ? 0xffffff : playerColor;
    const p = core.getInactive();
    if (!p) continue;
    configParticle(p, core, {
      x: spawnX,
      y: spawnY,
      vx: -Math.cos(angle) * speed,
      vy: -Math.sin(angle) * speed,
      color: moteColor,
      life: 0.25 + Math.random() * 0.1,
      scale: 0.3 + Math.random() * 0.2,
      alpha: 0.8,
      friction: 0.92,
      freezeImmune: true,
    });
  }

  // === Layer 2: Radial speed lines — Sakurai "straight lines vs curves" ===
  // Thin crisp lines radiating outward, creating geometric contrast against curved motes
  const lineCount = 10;
  for (let i = 0; i < lineCount; i++) {
    const angle = (i / lineCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
    const life = 0.12 + Math.random() * 0.06;
    core.impactLines.push({
      x: cx, y: cy,
      angle,
      innerRadius: 15,
      outerRadius: 40 + Math.random() * 20,
      expandSpeed: 80 + Math.random() * 40,
      color: Math.random() < 0.3 ? 0xffffff : playerColor,
      lineWidth: 1.5 + Math.random(),
      life,
      maxLife: life,
      freezeImmune: true,
    });
  }

  // === Layer 3: Dark contrast cavity — Sakurai "mix in darker elements" ===
  // Near-black particles behind the bright flash, creating depth
  for (let i = 0; i < 5; i++) {
    const angle = Math.random() * Math.PI * 2;
    const p = core.getInactive();
    if (!p) continue;
    configParticle(p, core, {
      x: cx + (Math.random() - 0.5) * 12,
      y: cy + (Math.random() - 0.5) * 12,
      vx: Math.cos(angle) * (0.5 + Math.random()),
      vy: Math.sin(angle) * (0.5 + Math.random()),
      color: 0x060610,
      life: 0.15 + Math.random() * 0.1,
      scale: 0.8 + Math.random() * 0.5,
      alpha: 0.5,
      friction: 0.9,
      rotationSpeed: 0,
      addBlend: false,
      hard: false, // soft texture — fades out as dark cloud
      freezeImmune: true,
    });
  }

  // === Layer 4: Core flash — bright soft glow at center, ultra-brief ===
  // Sakurai: "flash communicates impact instantly"
  const flash = core.getInactive();
  if (flash) {
    configParticle(flash, core, {
      x: cx,
      y: cy,
      color: playerColor,
      life: 0.12,
      scale: 3.5,
      alpha: 0.6,
      friction: 0,
      rotation: 0,
      rotationSpeed: 0,
      hard: false,
      freezeImmune: true,
    });
  }

  // === Layer 5: Delayed clockwise orbit sparks (+80ms) — Sakurai "stagger timing" ===
  // Spiral outward with slight clockwise bias — communicates energy gathering
  core.pendingBlasts.push({
    timestamp: Date.now() + 80,
    callback: () => {
      const orbitCount = 8;
      for (let i = 0; i < orbitCount; i++) {
        const angle = (i / orbitCount) * Math.PI * 2;
        const speed = 2 + Math.random() * 1.5;
        // Slight clockwise rotation to velocity
        const angVel = angle + Math.PI * 0.35; // offset 63° clockwise
        const sparkColor = Math.random() < 0.5 ? playerColor : 0xffffff;
        core.spawnParticleAtAngle(
          cx + Math.cos(angle) * 20,
          cy + Math.sin(angle) * 20,
          angVel, speed,
          sparkColor,
          0.3 + Math.random() * 0.15,
          0.25 + Math.random() * 0.15,
          0, 0.96,
          true, true, 0.85, 1.3,
        );
      }
    },
  });

  // === Layer 6: Expanding shockwave ring — anime time-freeze circle ===
  core.spawnShockwave(
    cx, cy,
    playerColor,
    60,   // ring expands to 60px — slightly larger than DI ring
    100,  // moderate expand speed
    2.5,  // medium thickness
    0.2,  // brief
  );

  // Second ring — delayed, thinner, white for contrast
  core.pendingBlasts.push({
    timestamp: Date.now() + 50,
    callback: () => {
      core.spawnShockwave(cx, cy, 0xffffff, 45, 130, 1.5, 0.15);
    },
  });
}

/**
 * DI Ambient — continuous idle energy around frozen tank during DI window.
 * Called every frame — spawns 1-2 particles per call for gentle ambient effect.
 *
 * Sakurai audit:
 * - Smaller is more — tiny motes, never overwhelming
 * - Mix dark + bright for background resilience
 * - Characters visible — low alpha, behind tank z-index
 */
export function spawnDIAmbient(core: ParticleCore, cx: number, cy: number, playerColor: number, kbAngle: number): void {
  // Stochastic spawn — ~30% chance per frame for gentle density
  if (Math.random() > 0.3) return;

  // Orbiting mote — spawns at ring distance, drifts clockwise
  const orbAngle = Math.random() * Math.PI * 2;
  const dist = 35 + Math.random() * 15;
  const tangent = orbAngle + Math.PI / 2; // perpendicular = clockwise drift

  const moteColor = Math.random() < 0.15 ? 0xffffff
    : Math.random() < 0.3 ? 0x333355 // dark motes (Sakurai: dark elements for depth)
      : playerColor;

  const addBlend = moteColor !== 0x333355;
  const p = core.getInactive();
  if (!p) return;
  configParticle(p, core, {
    x: cx + Math.cos(orbAngle) * dist,
    y: cy + Math.sin(orbAngle) * dist,
    vx: Math.cos(tangent) * (0.8 + Math.random() * 0.5),
    vy: Math.sin(tangent) * (0.8 + Math.random() * 0.5),
    color: moteColor,
    life: 0.4 + Math.random() * 0.3,
    scale: 0.12 + Math.random() * 0.12,
    alpha: moteColor === 0x333355 ? 0.5 : 0.6,
    friction: 0.98,
    addBlend,
    freezeImmune: false,
  });

  // Occasional perpendicular axis flicker — subtle visual cue along optimal DI direction
  if (Math.random() < 0.15) {
    const perpAngle = kbAngle + Math.PI / 2;
    const side = Math.random() < 0.5 ? 1 : -1;
    const flickerAngle = perpAngle + side * (Math.random() * 0.3);
    const flickerDist = 30 + Math.random() * 20;
    core.spawnParticleAtAngle(
      cx + Math.cos(flickerAngle) * flickerDist,
      cy + Math.sin(flickerAngle) * flickerDist,
      flickerAngle + side * Math.PI / 2, 0.5,
      playerColor,
      0.15 + Math.random() * 0.1,
      0.08 + Math.random() * 0.06,
      0, 0.9,
      true, true, 0.4,
    );
  }
}

/**
 * DI Input Active — directional energy burst when player holds a direction.
 * Called each frame while input is held — spawns fewer, more directional particles.
 *
 * Sakurai audit:
 * - Blade trail thinking — stretched speed lines in the direction of shift
 * - Contrasting dark wake behind bright streaks
 * - Short lifespans, rough and bold
 * - Proximity aggression near the DI direction
 */
export function spawnDIInputActive(
  core: ParticleCore,
  cx: number, cy: number,
  inputX: number, inputY: number,
  playerColor: number,
): void {
  // Stochastic — ~40% chance per frame (higher density than ambient)
  if (Math.random() > 0.4) return;

  const inputAngle = Math.atan2(inputY, inputX);
  const ringDist = 45; // spawn at ring edge

  // Bright directional streak — ADD blend, stretched along input direction
  const streakX = cx + Math.cos(inputAngle) * (ringDist + Math.random() * 15);
  const streakY = cy + Math.sin(inputAngle) * (ringDist + Math.random() * 15);
  const streakColor = Math.random() < 0.3 ? 0xffffff : 0x00ff88; // DI active green
  core.spawnParticleAtAngle(
    streakX, streakY,
    inputAngle + (Math.random() - 0.5) * 0.4, // slight spread
    2 + Math.random() * 3,
    streakColor,
    0.12 + Math.random() * 0.08,
    0.2 + Math.random() * 0.15,
    0, 0.93,
    true, true, 0.8,
    2.0 + Math.random(), // stretch 2-3x — anime speed lines
  );

  // Dark wake behind (opposing direction) — Sakurai dark contrast
  if (Math.random() < 0.35) {
    const wakeAngle = inputAngle + Math.PI; // behind the input
    const wakeDist = ringDist - 5 - Math.random() * 10;
    const p = core.getInactive();
    if (!p) return;
    configParticle(p, core, {
      x: cx + Math.cos(wakeAngle) * wakeDist,
      y: cy + Math.sin(wakeAngle) * wakeDist,
      vx: Math.cos(wakeAngle) * (0.5 + Math.random() * 0.5),
      vy: Math.sin(wakeAngle) * (0.5 + Math.random() * 0.5),
      color: 0x0a0c1e,
      life: 0.1 + Math.random() * 0.06,
      scale: 0.25 + Math.random() * 0.15,
      alpha: 0.4,
      friction: 0.9,
      rotationSpeed: 0,
      addBlend: false,
    });
  }

  // Perpendicular accent sparks — tiny bright sparks at +/-90deg from input
  if (Math.random() < 0.2) {
    const side = Math.random() < 0.5 ? 1 : -1;
    const accentAngle = inputAngle + side * Math.PI / 2;
    core.spawnParticleAtAngle(
      cx + Math.cos(inputAngle) * ringDist,
      cy + Math.sin(inputAngle) * ringDist,
      accentAngle, 1.5 + Math.random(),
      playerColor,
      0.08 + Math.random() * 0.06,
      0.1 + Math.random() * 0.08,
      0, 0.9,
      true, true, 0.7,
      1.5,
    );
  }
}

/**
 * DI Resolve — directional launch burst when DI window expires.
 * 7-layer Sakurai choreography: fanfare speed lines -> bright launch flash ->
 * dark contrast plume -> velocity-aligned streaks -> shockwave -> smoke baton pass -> gem confetti.
 *
 * Sakurai audit:
 * - Phase-sequenced (flash -> burst -> smoke -> end flash)
 * - Dark + bright contrast throughout
 * - Crisp lines + soft glow + hard particles — full range
 * - Scale: main flash spans 4x tank radius — significant moment
 * - Directional energy aligned with DI-shifted knockback
 * - Stagger timing (smoke at +100ms, confetti at +60ms)
 */
export function spawnDIResolve(
  core: ParticleCore,
  cx: number, cy: number,
  launchVx: number, launchVy: number,
  playerColor: number,
): void {
  const launchAngle = Math.atan2(launchVy, launchVx);
  const launchSpeed = Math.sqrt(launchVx * launchVx + launchVy * launchVy);
  const intensity = Math.min(1.0, launchSpeed / 10); // 0-1 based on knockback strength

  // === Layer 1: Fanfare speed lines — crisp radial lines in launch direction ===
  // Sakurai: "straight lines" in a directional fan, not 360°
  const lineCount = 6 + Math.floor(4 * intensity);
  for (let i = 0; i < lineCount; i++) {
    const spread = (Math.random() - 0.5) * Math.PI * 0.5; // +/-45deg fan
    const angle = launchAngle + spread;
    const life = 0.1 + Math.random() * 0.08;
    core.impactLines.push({
      x: cx, y: cy,
      angle,
      innerRadius: 10 + Math.random() * 5,
      outerRadius: (30 + Math.random() * 25) * intensity,
      expandSpeed: (100 + Math.random() * 60) * intensity,
      color: Math.random() < 0.3 ? 0xffffff : playerColor,
      lineWidth: 2 + intensity,
      life,
      maxLife: life,
      freezeImmune: false,
    });
  }

  // Opposing dark lines (Sakurai: dark elements behind bright for contrast)
  const darkLineCount = 3;
  for (let i = 0; i < darkLineCount; i++) {
    const spread = (Math.random() - 0.5) * Math.PI * 0.3;
    const angle = launchAngle + Math.PI + spread; // behind the launch
    const life = 0.08 + Math.random() * 0.04;
    core.impactLines.push({
      x: cx, y: cy,
      angle,
      innerRadius: 8,
      outerRadius: 20 + Math.random() * 10,
      expandSpeed: 60 + Math.random() * 30,
      color: 0x0a0c1e,
      lineWidth: 2.5,
      life,
      maxLife: life,
      freezeImmune: false,
    });
  }

  // === Layer 2: Bright launch flash — large soft glow at center ===
  // Sakurai: "flash communicates impact instantly"
  const flash = core.getInactive();
  if (flash) {
    configParticle(flash, core, {
      x: cx,
      y: cy,
      vx: Math.cos(launchAngle) * 1,
      vy: Math.sin(launchAngle) * 1,
      color: playerColor,
      life: 0.15,
      scale: 4.0 * intensity,
      alpha: 0.7,
      friction: 0.5,
      rotation: 0,
      rotationSpeed: 0,
      hard: false,
    });
  }

  // === Layer 3: Dark contrast plume — near-black particles behind bright launch ===
  // Sakurai: "dark cavity behind bright blast provides depth"
  const darkCount = 4 + Math.floor(3 * intensity);
  for (let i = 0; i < darkCount; i++) {
    const backAngle = launchAngle + Math.PI + (Math.random() - 0.5) * Math.PI * 0.6;
    const p = core.getInactive();
    if (!p) continue;
    configParticle(p, core, {
      x: cx + (Math.random() - 0.5) * 10,
      y: cy + (Math.random() - 0.5) * 10,
      vx: Math.cos(backAngle) * (1 + Math.random() * 2),
      vy: Math.sin(backAngle) * (1 + Math.random() * 2),
      color: 0x060610,
      life: 0.2 + Math.random() * 0.15,
      scale: 0.4 + Math.random() * 0.4,
      alpha: 0.5,
      gravity: 0.04,
      friction: 0.94,
      addBlend: false,
      hard: false,
    });
  }

  // === Layer 4: Velocity-aligned streaks — anime speed lines in launch direction ===
  // Sakurai: "blade trail thinking" — communicates directional energy
  const streakCount = 8 + Math.floor(6 * intensity);
  for (let i = 0; i < streakCount; i++) {
    const spread = (Math.random() - 0.5) * Math.PI * 0.4; // +/-36deg cone
    const angle = launchAngle + spread;
    const speed = (4 + Math.random() * 6) * intensity;
    const streakColor = Math.random() < 0.25 ? 0xffffff
      : Math.random() < 0.4 ? 0x00ff88  // DI green
        : playerColor;
    const dist = 15 + Math.random() * 10;
    // Pre-offset spawn position along launch (spawnParticleAtAngle has no offset param)
    const offX = cx + Math.cos(launchAngle) * dist + Math.cos(angle) * (Math.random() * 5);
    const offY = cy + Math.sin(launchAngle) * dist + Math.sin(angle) * (Math.random() * 5);
    core.spawnParticleAtAngle(
      offX, offY,
      angle, speed,
      streakColor,
      0.1 + Math.random() * 0.12,
      (0.3 + Math.random() * 0.3) * intensity,
      0.03, 0.95,
      true, true, 0.9,
      2.5 + Math.random() * 1.5, // stretch 2.5-4x — anime speed feel
    );
  }

  // === Layer 5: DI result shockwave — directional, not circular ===
  // Asymmetric ring biased in launch direction
  core.spawnShockwave(
    cx + Math.cos(launchAngle) * 10,
    cy + Math.sin(launchAngle) * 10,
    playerColor,
    40 * intensity,
    140,
    2,
    0.15,
  );

  // === Layer 6: Smoke baton pass (+100ms) — Sakurai phase sequencing ===
  // Dark, slow, expanding — takes over as bright elements fade
  core.pendingBlasts.push({
    timestamp: Date.now() + 100,
    callback: () => {
      const smokeCount = 4 + Math.floor(3 * intensity);
      for (let i = 0; i < smokeCount; i++) {
        const angle = launchAngle + Math.PI + (Math.random() - 0.5) * Math.PI * 0.8;
        const dist = 8 + Math.random() * 12;
        core.spawnParticle(
          cx + Math.cos(angle) * dist,
          cy + Math.sin(angle) * dist,
          Math.random() < 0.5 ? 0x0a0c18 : 0x121428,
          0.3, 1.5,
          0.4, 0.8,          // longer life — smoke lingers
          0.8, 1.5,          // large, soft
          -0.01,             // slight upward float
          0.985,
          false, false,      // NORMAL + soft — dark smoke
          0.35,
        );
      }
    },
  });

  // === Layer 7: Gem confetti (+60ms) — celebratory sparkle ===
  // Sakurai: "stagger timing" + multi-element billboard
  core.pendingBlasts.push({
    timestamp: Date.now() + 60,
    callback: () => {
      const gemCount = 6 + Math.floor(4 * intensity);
      const palette = GEM_PALETTES[playerColor] || DEFAULT_GEM_PALETTE;
      for (let i = 0; i < gemCount; i++) {
        const angle = launchAngle + (Math.random() - 0.5) * Math.PI * 0.7;
        const speed = 3 + Math.random() * 5;
        const gemColor = palette[Math.floor(Math.random() * palette.length)];
        const p = core.getInactive();
        if (!p) continue;
        configParticle(p, core, {
          x: cx + Math.cos(launchAngle) * (10 + Math.random() * 15),
          y: cy + Math.sin(launchAngle) * (10 + Math.random() * 15),
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          color: gemColor,
          life: 0.3 + Math.random() * 0.25,
          scale: 0.15 + Math.random() * 0.15,
          alpha: 0.9,
          gravity: 0.12,
          friction: 0.97,
        });
        p.sprite.texture = core.clusterTexture; // multi-gem billboard (Sakurai trick)
      }
    },
  });

  // === Layer 8: End flash (+160ms) — Sakurai Phase 4 punctuation ===
  // Brief bright pop to cleanly terminate the sequence
  core.pendingBlasts.push({
    timestamp: Date.now() + 160,
    callback: () => {
      const endFlash = core.getInactive();
      if (endFlash) {
        configParticle(endFlash, core, {
          x: cx + Math.cos(launchAngle) * 15,
          y: cy + Math.sin(launchAngle) * 15,
          color: 0xffffff,
          life: 0.06,
          scale: 3.0 * intensity,
          alpha: 0.6,
          rotation: 0,
          rotationSpeed: 0,
          hard: false,
        });
      }
      // Dark counterpart (Sakurai: mix black into flash)
      const darkPop = core.getInactive();
      if (darkPop) {
        configParticle(darkPop, core, {
          x: cx,
          y: cy,
          color: 0x060610,
          life: 0.08,
          scale: 2.0 * intensity,
          alpha: 0.4,
          rotation: 0,
          rotationSpeed: 0,
          addBlend: false,
          hard: false,
        });
      }
    },
  });
}
